import { useEffect, useMemo, useRef, useState } from "react";
import { useAppAuth } from "@/auth/use-app-auth";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Axios } from "@/lib/axios";
import {
  clearAuthReturnTo,
  clearInvitationToken,
  opensBeforeOnboarding,
  persistAuthReturnTo,
  readAuthReturnTo,
  readInvitationToken,
} from "@/lib/auth-flow";

const sanitizeReturnTo = (value: string | null) => {
  if (!value) return "/dashboard";
  // Same-origin paths only ("//host" would leave the site).
  return value.startsWith("/") && !value.startsWith("//") ? value : "/dashboard";
};

const AuthCallback = () => {
  const {
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    loginWithRedirect,
    setActiveOrganization,
  } = useAppAuth();
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const acceptingInvitationRef = useRef(false);

  const returnTo = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return sanitizeReturnTo(params.get("returnTo") || readAuthReturnTo());
  }, [location.search]);

  useEffect(() => {
    const resolveDestination = async () => {
      if (isLoading) return;

      if (!isAuthenticated) {
        persistAuthReturnTo(returnTo);
        await loginWithRedirect({
          appState: { returnTo },
        });
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const invitationToken = readInvitationToken();

        if (invitationToken) {
          if (acceptingInvitationRef.current) return;
          acceptingInvitationRef.current = true;
          const acceptResponse = await Axios.post(
            "/api/auth/invitations/accept",
            { token: invitationToken },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          clearInvitationToken();

          const clerkOrgId = acceptResponse.data?.clerkOrgId;
          if (clerkOrgId) {
            await setActiveOrganization(clerkOrgId);
            clearAuthReturnTo();
            navigate("/org-setup/onboarding");
            return;
          }
        }

        const response = await Axios.get("/api/auth/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.data?.onboardingCompleted) {
          clearAuthReturnTo();
          navigate(opensBeforeOnboarding(returnTo) ? returnTo : "/org-setup/onboarding");
          return;
        }

        clearAuthReturnTo();
        navigate(returnTo || "/dashboard");
      } catch (err: any) {
        const status = err?.response?.status;
        const message = err?.response?.data?.message;

        if (status === 404 || message === "Recruiter not found") {
          clearAuthReturnTo();
          navigate(opensBeforeOnboarding(returnTo) ? returnTo : "/org-setup/onboarding");
          return;
        }

        console.error("Auth callback error:", err);
        setError(message || "We ran into an issue signing you in. Please try again.");
      }
    };

    resolveDestination();
  }, [
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    navigate,
    returnTo,
    setActiveOrganization,
  ]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-4 rounded-2xl border border-slate-200 bg-white px-8 py-10 shadow-sm">
        <Loader2 className="size-10 animate-spin text-amber-600" />
        <div className="text-center">
          <h1 className="text-xl font-semibold text-slate-900">Signing you in</h1>
          <p className="text-sm text-slate-500">Setting up your account workspace...</p>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </div>
  );
};

export default AuthCallback;
